"use client";

import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { Languages, Moon, Sun } from "lucide-react";
import type { Dictionary } from "@/dictionaries/types";
import type { Locale } from "@/i18n-config";
import { getMobileDrawerNav, groupNavItems, type NavViewerState } from "@/lib/navigation";
import { v2Surface } from "./nav-v2";
import { useTheme } from "@/hooks/useTheme";
import { Dialog } from "./ui";
import "./menu.css";

/**
 * THE PHONE MENU.
 *
 * The rail is the only place the theme and locale controls live on desktop,
 * and the rail starts at 1024px. Without this sheet, taking those two buttons
 * out of the top bar would strand a phone user with no way to switch either —
 * so the sheet is not a nicety, it is what makes that removal safe.
 *
 * Its nav list is the catalogue's `mobileDrawer` surface, which is the same
 * surface the legacy Quick Menu used. Nothing is re-listed here by hand: a
 * route that a signed-in viewer may reach appears because the catalogue says
 * so, and disappears for a guest because `resolveNavAudience` derives that
 * from the middleware's protected list.
 *
 * WHAT IS LISTED IS INHERITED; HOW IT LOOKS IS NOT. The entries, their URLs
 * and their grouping come from the catalogue; the icons and every pixel of
 * the presentation come from this design system, through `v2Surface`. Taking
 * the old chrome's icons along with its item list is the specific mistake
 * that rule exists to prevent.
 */

export default function MenuSheet(t0) {
  const $ = _c(54);
  const {
    open,
    onClose,
    lang,
    dict,
    viewer,
    activeId,
    languageHref
  } = t0;
  const isFa = lang === "fa";
  const {
    theme,
    toggleTheme
  } = useTheme();
  let T0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== activeId || $[1] !== dict || $[2] !== isFa || $[3] !== lang || $[4] !== onClose || $[5] !== open || $[6] !== viewer) {
    const sections = groupNavItems(v2Surface(getMobileDrawerNav(viewer, lang, dict), lang, dict));
    T0 = Dialog;
    t3 = open;
    t4 = onClose;
    t5 = dict.navUi.quickMenu;
    t6 = isFa ? "\u0628\u0633\u062A\u0646" : "Close";
    t7 = "v2-menu-sheet";
    t8 = true;
    t9 = false;
    t1 = dict.navUi.mobileNavigation;
    let t10;
    if ($[17] !== activeId || $[18] !== dict.navGroups || $[19] !== onClose) {
      t10 = section => <div className="v2-menu-group" key={section.group}><p className="v2-menu-head"><i aria-hidden={true} />{dict.navGroups[section.group]}</p><div className="v2-menu-list">{section.items.map(item => {
            const Icon = item.icon;
            return <Link className="v2-menu-link v2-focus" href={item.href} key={item.id} onClick={onClose} aria-current={item.id === activeId ? "page" : undefined}><Icon aria-hidden={true} /><span>{item.label}</span></Link>;
          })}</div></div>;
      $[17] = activeId;
      $[18] = dict.navGroups;
      $[19] = onClose;
      $[20] = t10;
    } else {
      t10 = $[20];
    }
    t2 = sections.map(t10);
    $[0] = activeId;
    $[1] = dict;
    $[2] = isFa;
    $[3] = lang;
    $[4] = onClose;
    $[5] = open;
    $[6] = viewer;
    $[7] = T0;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
  } else {
    T0 = $[7];
    t1 = $[8];
    t2 = $[9];
    t3 = $[10];
    t4 = $[11];
    t5 = $[12];
    t6 = $[13];
    t7 = $[14];
    t8 = $[15];
    t9 = $[16];
  }
  let t10;
  if ($[21] !== t1 || $[22] !== t2) {
    t10 = <nav aria-label={t1}>{t2}</nav>;
    $[21] = t1;
    $[22] = t2;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== theme) {
    t11 = theme === "dark" ? <Sun aria-hidden={true} /> : <Moon aria-hidden={true} />;
    $[24] = theme;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  const t12 = isFa ? "\u062A\u0645 \u0631\u0648\u0634\u0646/\u062A\u0627\u0631\u06CC\u06A9" : "Light / dark";
  let t13;
  if ($[26] !== t12) {
    t13 = <span>{t12}</span>;
    $[26] = t12;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== t11 || $[29] !== t13 || $[30] !== toggleTheme) {
    t14 = <button type="button" className="v2-btn v2-btn--sm v2-focus" onClick={toggleTheme}>{t11}{t13}</button>;
    $[28] = t11;
    $[29] = t13;
    $[30] = toggleTheme;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  const t15 = isFa ? "en" : "fa";
  let t16;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <Languages aria-hidden={true} />;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  const t17 = isFa ? "English" : "\u0641\u0627\u0631\u0633\u06CC";
  let t18;
  if ($[33] !== t17) {
    t18 = <span>{t17}</span>;
    $[33] = t17;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] !== languageHref || $[36] !== onClose || $[37] !== t15 || $[38] !== t18) {
    t19 = <Link className="v2-btn v2-btn--sm v2-focus" href={languageHref} hrefLang={t15} onClick={onClose}>{t16}{t18}</Link>;
    $[35] = languageHref;
    $[36] = onClose;
    $[37] = t15;
    $[38] = t18;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] !== t14 || $[41] !== t19) {
    t20 = <div className="v2-menu-tools">{t14}{t19}</div>;
    $[40] = t14;
    $[41] = t19;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  let t21;
  if ($[43] !== T0 || $[44] !== t10 || $[45] !== t20 || $[46] !== t3 || $[47] !== t4 || $[48] !== t5 || $[49] !== t6 || $[50] !== t7 || $[51] !== t8 || $[52] !== t9) {
    t21 = <T0 open={t3} onClose={t4} title={t5} closeLabel={t6} className={t7} hideClose={t8} modal={t9}>{t10}{t20}</T0>;
    $[43] = T0;
    $[44] = t10;
    $[45] = t20;
    $[46] = t3;
    $[47] = t4;
    $[48] = t5;
    $[49] = t6;
    $[50] = t7;
    $[51] = t8;
    $[52] = t9;
    $[53] = t21;
  } else {
    t21 = $[53];
  }
  return t21;
}